import { useState, useEffect } from "react";
import {
  ArrowRight,
  Calendar,
  User,
  Tag,
  Bookmark,
  Search,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Heart,
  Clock,
  Eye,
  Share2,
  BookOpen,
  Trending,
  Coffee,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const EnhancedBlogPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [featuredBlogs, setFeaturedBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const ITEMS_PER_PAGE = 6;

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setIsLoading(true);
        const response = await fetch("https://apigulmohar.dodunsoftsolutions.com/api/blogs");

        if (!response.ok) {
          throw new Error("Failed to fetch blogs");
        }

        const data = await response.json();
        setBlogs(data);

        // Set featured blogs
        const featured = data.filter(
          (blog) => blog.isFeatured && blog.status === "published"
        );
        setFeaturedBlogs(featured.slice(0, 3));

        setIsLoading(false);
      } catch (err) {
        setError(err.message);
        setIsLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // Get all unique tags for categories
  const allCategories = [
    "All",
    ...new Set(blogs.flatMap((blog) => blog.tags || [])),
  ];

  // Filter blogs based on active category and search query
  const filteredBlogs = blogs.filter((blog) => {
    const matchesCategory =
      activeCategory === "All" ||
      (blog.tags && blog.tags.includes(activeCategory));
    const matchesSearch =
      searchQuery === "" ||
      blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (blog.excerpt &&
        blog.excerpt.toLowerCase().includes(searchQuery.toLowerCase())) ||
      blog.author.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  // Sort blogs based on sortBy value
  const sortedBlogs = [...filteredBlogs].sort((a, b) => {
    if (sortBy === "newest") {
      return new Date(b.publishedAt) - new Date(a.publishedAt);
    } else if (sortBy === "oldest") {
      return new Date(a.publishedAt) - new Date(b.publishedAt);
    } else if (sortBy === "popular") {
      // Assuming there's a views property, otherwise default to 0
      return (b.views || 0) - (a.views || 0);
    }
    return 0;
  });

  // Paginate blogs
  const totalPages = Math.ceil(sortedBlogs.length / ITEMS_PER_PAGE);
  const paginatedBlogs = sortedBlogs.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Handle keyboard navigation for accessibility
  const handleKeyPress = (e, action) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      action();
    }
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const getReadTime = (content) => {
    // Assuming an average reading speed of 200 words per minute
    const wordCount = content ? content.split(/\s+/).length : 0;
    const readTime = Math.ceil(wordCount / 200);
    return readTime > 0 ? readTime : 1; // Minimum 1 minute read time
  };

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "...";
  };

  const handleBlogClick = (id) => {
    navigate(`/view-blog/${id}`);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % featuredBlogs.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + featuredBlogs.length) % featuredBlogs.length
    );
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({
      top: document.getElementById("blog-grid").offsetTop - 100,
      behavior: "smooth",
    });
  };

  // Loading state with skeleton loaders
  if (isLoading) {
    return (
      <div
        className={`bg-gradient-to-br from-gray-200 to-cyan-200 min-h-screen font-sans transition-colors duration-300`}
      >
        {/* Header Skeleton */}
        <header
          className={`bg-white/80 border-gray-100 backdrop-blur-md sticky top-0 z-10 border-b shadow-sm transition-colors duration-300`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="h-8 w-8 rounded-full bg-gray-200 animate-pulse"></div>
                <div className="h-6 w-32 bg-gray-200 rounded-md animate-pulse"></div>
              </div>

              <div className="hidden md:flex items-center space-x-4">
                <div className="h-8 w-16 bg-gray-200 rounded-md animate-pulse"></div>
                <div className="h-8 w-20 bg-gray-200 rounded-md animate-pulse"></div>
                <div className="h-8 w-16 bg-gray-200 rounded-md animate-pulse"></div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="w-32 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                <div className="md:hidden h-8 w-8 rounded-md bg-gray-200 animate-pulse"></div>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section Skeleton */}
        <div className="relative bg-gradient-to-r from-gray-900 to-cyan-900 h-96 md:h-[500px] animate-pulse">
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-16 pt-32">
            <div className="max-w-2xl">
              <div className="h-6 w-24 bg-white/20 rounded-full mb-6"></div>
              <div className="h-12 w-full bg-white/20 rounded-md mb-4"></div>
              <div className="h-12 w-3/4 bg-white/20 rounded-md mb-4"></div>
              <div className="h-6 w-full bg-white/20 rounded-md mb-4"></div>
              <div className="h-6 w-5/6 bg-white/20 rounded-md mb-8"></div>
              <div className="flex items-center space-x-6 mb-8">
                <div className="flex items-center space-x-2">
                  <div className="h-10 w-10 rounded-full bg-white/20"></div>
                  <div>
                    <div className="h-4 w-24 bg-white/20 rounded mb-2"></div>
                    <div className="h-3 w-32 bg-white/20 rounded"></div>
                  </div>
                </div>
              </div>
              <div className="h-10 w-32 bg-white/20 rounded-md"></div>
            </div>
          </div>
        </div>

        {/* Main Content Skeleton */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-8">
            <div className="h-8 w-48 bg-gray-200 rounded mb-4 animate-pulse"></div>
            <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
          </div>

          <div className="hidden md:flex flex-wrap items-center gap-2 mb-8">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="h-8 w-20 bg-gray-200 rounded-full animate-pulse"
              ></div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className={`bg-white rounded-xl overflow-hidden shadow-md h-full animate-pulse transition-colors duration-300`}
              >
                <div className="aspect-video bg-gray-200"></div>
                <div className="p-6">
                  <div className="flex space-x-2 mb-3">
                    <div className="h-4 w-16 bg-gray-200 rounded-full"></div>
                    <div className="h-4 w-16 bg-gray-200 rounded-full"></div>
                  </div>
                  <div className="h-6 w-full bg-gray-200 rounded mb-2"></div>
                  <div className="h-6 w-3/4 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 w-full bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 w-full bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 w-2/3 bg-gray-200 rounded mb-4"></div>
                  <div className="h-6 w-full bg-gray-200 rounded-md mt-6"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state with improved styling
  if (error) {
    return (
      <div
        className={`flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-200 to-cyan-200 transition-colors duration-300`}
      >
        <div
          className={`bg-white border-red-100 shadow-xl rounded-2xl p-8 max-w-md border`}
        >
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <X className="h-8 w-8 text-red-600" />
          </div>
          <h3 className={`text-2xl font-bold text-center text-gray-900 mb-2`}>
            Connection Error
          </h3>
          <p className={`text-gray-600 text-center mb-6`}>{error}</p>
          <p className="text-sm text-gray-500 text-center mb-4">
            Please make sure your API is running at
            https://apigulmohar.dodunsoftsolutions.com/api/blogs
          </p>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-gray-600 text-white py-3 rounded-lg font-medium hover:bg-gray-700 transition duration-200 shadow-md"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-slate-800/30 text-gray-900 min-h-screen font-sans transition-colors duration-300`}
    >
      {/* Featured Carousel */}
      {featuredBlogs.length > 0 && (
        <div className="relative overflow-hidden bg-gradient-to-r from-gray-900 to-cyan-900">
          {featuredBlogs.map((blog, index) => (
            <div
              key={blog._id}
              className={`transition-all duration-500 ${
                index === currentSlide
                  ? "opacity-100"
                  : "opacity-0 absolute inset-0"
              }`}
              style={{ display: index === currentSlide ? "block" : "none" }}
            >
              <div className="relative h-[680px]">
                <div className="absolute inset-0">
                  <img
                    src={blog.coverImage || "/api/placeholder/1600/900"}
                    alt={blog.title}
                    className="w-full h-full object-cover opacity-40"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/70 to-transparent mix-blend-multiply"></div>
                </div>
                <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-16 pt-32">
                  <div className="max-w-2xl">
                    <div className="mb-6 flex items-center space-x-2">
                      <span className="bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                        FEATURED
                      </span>
                      {blog.tags &&
                        blog.tags.slice(0, 1).map((tag) => (
                          <span
                            key={tag}
                            className="bg-white/20 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                    </div>
                    <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
                      {blog.title}
                    </h1>
                    <p className="text-gray-300 text-lg mb-8 line-clamp-2 md:line-clamp-3">
                      {blog.excerpt}
                    </p>
                    <div className="flex items-center space-x-6 mb-8">
                      <div className="flex items-center space-x-2">
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                          <User className="h-6 w-6 text-gray-800" />
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {blog.author}
                          </p>
                          <p className="text-gray-400 text-sm">
                            {formatDate(blog.publishedAt)}
                          </p>
                        </div>
                      </div>
                      <div className="text-gray-400 text-sm flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {getReadTime(blog.content || blog.excerpt)} min read
                      </div>
                    </div>
                    <button
                      onClick={() => handleBlogClick(blog._id)}
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-md text-gray-900 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition duration-200"
                    >
                      Read Article
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {featuredBlogs.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-4 md:left-8 top-1/2 transform -translate-y-1/2 h-10 w-10 bg-white/20 backdrop-blur-sm text-white rounded-full flex items-center justify-center hover:bg-white/30 transition duration-200"
                aria-label="Previous slide"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 md:right-8 top-1/2 transform -translate-y-1/2 h-10 w-10 bg-white/20 backdrop-blur-sm text-white rounded-full flex items-center justify-center hover:bg-white/30 transition duration-200"
                aria-label="Next slide"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {featuredBlogs.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`h-2 w-8 rounded-full ${
                      index === currentSlide ? "bg-gray-400" : "bg-white/30"
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Section Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h2
              className={`text-3xl font-bold text-gray-900 mb-2 flex items-center pt-28`}
            >
              <span className="border-b-4 border-amber-400 pb-1">
                {activeCategory === "All" ? "Latest Articles" : activeCategory}
              </span>
            </h2>
            <p className={`text-gray-600`}>
              {filteredBlogs.length}{" "}
              {filteredBlogs.length === 1 ? "article" : "articles"} available
            </p>
          </div>

          <div className="mt-4 md:mt-0 flex flex-wrap gap-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-32 md:w-64 pl-8 sm:pl-10 pr-2 sm:pr-4 py-2 border border-gray-200 focus:ring-amber-500 focus:border-amber-500 bg-white/70 rounded-full text-sm focus:ring-2 transition-colors duration-200`}
              />
              <Search className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-gray-600" />
            </div>
            {/* View toggle */}
            <div
              className={`flex rounded-lg overflow-hidden border border-gray-200 shadow-sm`}
            >
              <button
                onClick={() => setViewMode("grid")}
                className={`px-3 py-2 ${
                  viewMode === "grid"
                    ? "bg-amber-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
                aria-label="Grid view"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                  />
                </svg>
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`px-3 py-2 ${
                  viewMode === "list"
                    ? "bg-amber-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
                aria-label="List view"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </div>

            {/* Sort dropdown */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className={`bg-white border-gray-200 text-gray-700 border py-2 px-3 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-colors duration-200`}
              aria-label="Sort articles"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="popular">Most Popular</option>
            </select>
          </div>
        </div>

        {/* Category Pills */}
        <div className="hidden md:flex flex-wrap items-center gap-2 mb-8">
          {allCategories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                activeCategory === category
                  ? "bg-amber-600 text-white shadow-md"
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
              } transition duration-200`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Blog Grid/List */}
        <div id="blog-grid" className="mt-8">
          {filteredBlogs.length === 0 ? (
            <div
              className={`text-center py-16 bg-white border-gray-100 rounded-xl shadow-sm border`}
            >
              <Bookmark className={`h-16 w-16 text-gray-300 mx-auto mb-4`} />
              <h3 className={`text-2xl font-bold text-gray-900' mb-2`}>
                No articles found
              </h3>
              <p className={`text-gray-500 mb-8 max-w-md mx-auto`}>
                We couldn't find any articles matching your current filters. Try
                adjusting your search criteria.
              </p>
              <button
                onClick={() => {
                  setActiveCategory("All");
                  setSearchQuery("");
                }}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200 shadow-md"
              >
                View All Articles
              </button>
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {paginatedBlogs.map((blog) => (
                <div
                  key={blog._id}
                  onClick={() => handleBlogClick(blog._id)}
                  onKeyPress={(e) =>
                    handleKeyPress(e, () => handleBlogClick(blog._id))
                  }
                  tabIndex="0"
                  role="button"
                  aria-label={`Read article: ${blog.title}`}
                  className="group cursor-pointer focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 rounded-xl"
                >
                  <div
                    className={`bg-white border-gray-100 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition duration-300 h-full flex flex-col border`}
                  >
                    <div className="relative aspect-video overflow-hidden bg-gray-100">
                      <img
                        src={blog.coverImage || "/api/placeholder/600/400"}
                        alt={blog.title}
                        className="w-full h-full object-cover transform group-hover:scale-105 transition duration-500"
                      />
                      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                      {blog.status !== "published" && (
                        <div className="absolute top-3 left-3 bg-gray-900/80 backdrop-blur-sm text-white text-xs font-medium px-2.5 py-1 rounded-full">
                          {blog.status}
                        </div>
                      )}
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex-1">
                        <div className="flex flex-wrap gap-2 mb-3">
                          {blog.tags &&
                            blog.tags.map((tag, index) =>
                              index < 3 ? (
                                <span
                                  key={index}
                                  className={`bg-gray-50 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full`}
                                >
                                  {tag}
                                </span>
                              ) : null
                            )}
                          {blog.tags && blog.tags.length > 3 && (
                            <span
                              className={`bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full`}
                            >
                              +{blog.tags.length - 3}
                            </span>
                          )}
                        </div>
                        <h3
                          className={`text-xl font-bold mb-3 text-gray-900 group-hover:text-gray-600 transition duration-200 line-clamp-2`}
                        >
                          {blog.title}
                        </h3>
                        {blog.excerpt && (
                          <p
                            className={`text-gray-600 text-sm mb-4 line-clamp-3`}
                          >
                            {blog.excerpt}
                          </p>
                        )}
                      </div>
                      <div
                        className={`flex items-center justify-between pt-4 border-t border-gray-100`}
                      >
                        <div className="flex items-center space-x-1">
                          <User className="h-4 w-4 text-gray-500" />
                          <span className={`text-sm text-gray-600`}>
                            {blog.author}
                          </span>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-1">
                            <Clock className="h-4 w-4 text-gray-500" />
                            <span className={`text-sm text-gray-600`}>
                              {getReadTime(blog.content || blog.excerpt)} min
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-4 w-4 text-gray-500" />
                            <span className={`text-sm text-gray-600`}>
                              {formatDate(blog.publishedAt).split(",")[0]}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              {paginatedBlogs.map((blog) => (
                <div
                  key={blog._id}
                  onClick={() => handleBlogClick(blog._id)}
                  onKeyPress={(e) =>
                    handleKeyPress(e, () => handleBlogClick(blog._id))
                  }
                  tabIndex="0"
                  role="button"
                  aria-label={`Read article: ${blog.title}`}
                  className={`bg-white border-gray-100 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition duration-300 border flex flex-col md:flex-row cursor-pointer focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 group`}
                >
                  <div className="w-full md:w-1/3 lg:w-1/4 relative">
                    <img
                      src={blog.coverImage || "/api/placeholder/600/400"}
                      alt={blog.title}
                      className="w-full h-48 md:h-full object-cover"
                    />
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    {blog.status !== "published" && (
                      <div className="absolute top-3 left-3 bg-gray-900/80 backdrop-blur-sm text-white text-xs font-medium px-2.5 py-1 rounded-full">
                        {blog.status}
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {blog.tags &&
                          blog.tags.map((tag, index) =>
                            index < 3 ? (
                              <span
                                key={index}
                                className={`bg-gray-50 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full`}
                              >
                                {tag}
                              </span>
                            ) : null
                          )}
                        {blog.tags && blog.tags.length > 3 && (
                          <span
                            className={`bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full`}
                          >
                            +{blog.tags.length - 3}
                          </span>
                        )}
                      </div>
                      <h3
                        className={`text-xl font-bold mb-3 text-gray-900 group-hover:text-gray-600 transition duration-200`}
                      >
                        {blog.title}
                      </h3>
                      {blog.excerpt && (
                        <p className={`text-gray-600 mb-4 line-clamp-2`}>
                          {blog.excerpt}
                        </p>
                      )}
                    </div>

                    <div
                      className={`flex flex-wrap items-center gap-x-6 gap-y-2 mt-4 text-sm text-gray-500`}
                    >
                      <div className="flex items-center space-x-1">
                        <User className="h-4 w-4 text-gray-500" />
                        <span>{blog.author}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span>
                          {formatDate(blog.publishedAt).split(",")[0]}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4 text-gray-500" />
                        <span>
                          {getReadTime(blog.content || blog.excerpt)} min read
                        </span>
                      </div>
                      <div className="flex items-center ml-auto">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBlogClick(blog._id);
                          }}
                          className={`mt-2 md:mt-0 text-gray-600 hover:text-gray-700 font-medium flex items-center transition-colors`}
                        >
                          Read Article
                          <ArrowRight className="ml-1 h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-12 flex items-center justify-center">
              <nav
                className="relative z-0 inline-flex shadow-sm rounded-md"
                aria-label="Pagination"
              >
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-3 py-2 rounded-l-md bg-white border-gray-300'
                    } border text-sm font-medium ${
                      currentPage === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-gray-700 hover:bg-gray-50"
                    } transition-colors duration-200`}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>

                {Array.from({ length: totalPages }).map((_, index) => {
                  const pageNumber = index + 1;
                  // Show first page, last page, current page, and pages around current page
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 &&
                      pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`relative inline-flex items-center px-4 py-2 border-gray-300 border text-sm font-medium ${
                          pageNumber === currentPage
                            ? "bg-gray-600 text-white border-gray-600 z-10"
                            : "bg-white text-gray-700 hover:bg-gray-50"
                        } transition-colors duration-200`}
                        aria-label={`Page ${pageNumber}`}
                        aria-current={
                          pageNumber === currentPage ? "page" : undefined
                        }
                      >
                        {pageNumber}
                      </button>
                    );
                  }

                  // Show ellipsis
                  if (
                    (pageNumber === 2 && currentPage > 3) ||
                    (pageNumber === totalPages - 1 &&
                      currentPage < totalPages - 2)
                  ) {
                    return (
                      <span
                        key={pageNumber}
                        className={`relative inline-flex items-center px-4 py-2 border-gray-300 bg-white text-gray-700 border text-sm font-medium`}
                      >
                        ...
                      </span>
                    );
                  }

                  return null;
                })}

                <button
                  onClick={() =>
                    handlePageChange(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-3 py-2 rounded-r-md bg-white border-gray-300 border text-sm font-medium ${
                    currentPage === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-700 hover:bg-gray-50"
                  } transition-colors duration-200`}
                  aria-label="Next page"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </nav>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default EnhancedBlogPage;
